import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Dropdown,
  Empty,
  Image,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, DownOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CopyableText from "components/common/CopyableText";
import useFormattedDate from "hooks/useFormatDate";
import olreadiaService from "services/olreadiaService";
import { STORAGE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import { useUserManagement } from "hooks";

const { Text } = Typography;

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const isImageUrl = (url) => /\.(png|jpe?g|webp|gif)(\?|$)/i.test(String(url || ""));
const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ""));

const parseMaybeDate = (value) => {
  const d = value ? dayjs(value) : null;
  return d && d.isValid() ? d : null;
};

const equalsCode = (left, right) => {
  if (!left || !right) return false;
  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
};

const jsonArrayContainsCode = (node, code) => {
  if (!code) return false;
  const needle = String(code).trim();
  if (!needle) return false;

  const items = toArray(node);
  if (!items.length) return false;

  const matches = (value) => equalsCode(value, needle);

  for (const item of items) {
    if (item == null) continue;
    if (typeof item === "string" || typeof item === "number") {
      if (matches(item)) return true;
      continue;
    }
    if (typeof item === "object") {
      if (
        matches(item.id) ||
        matches(item.code) ||
        matches(item.caption) ||
        matches(item.name) ||
        matches(item.type) ||
        matches(item.documentType) ||
        matches(item.certificationType) ||
        matches(item.issuer) ||
        matches(item.certificationNumber)
      ) {
        return true;
      }
    }
  }

  return false;
};

const requirementSatisfiedByDocuments = (profile, requirementCode) =>
  jsonArrayContainsCode(profile?.documents, requirementCode);

const requirementSatisfiedByCertifications = (profile, requirementCode) =>
  jsonArrayContainsCode(profile?.certifications, requirementCode);

const requirementSatisfiedByInformation = (profile, requirementCode) => {
  const code = String(requirementCode || "").trim().toUpperCase();
  if (code === "EXPERIENCE") return Boolean(String(profile?.experience || "").trim());
  if (code === "PROFILE_SUMMARY" || code === "SUMMARY") return Boolean(String(profile?.profileSummary || "").trim());
  // Backend currently checks summary presence for INFORMATION requirements.
  return Boolean(String(profile?.profileSummary || "").trim());
};

const formatLocationInterest = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const city = value.city || "";
    const state = value.state || "";
    const country = value.country || "";
    const county = value.county || "";
    return [city, state, county, country].filter(Boolean).join(", ");
  }
  return String(value);
};

const normalizeAttachment = (value) => {
  if (!value) return null;
  if (typeof value === "string") return { fileId: value, name: value };
  return value;
};

const extractFileId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    return (
      value.fileId ||
      value.fid ||
      value.file_id ||
      value.id ||
      value.file?.fileId ||
      value.file?.id ||
      null
    );
  }
  return null;
};

const extractFileName = (value, fallback) => {
  if (!value) return fallback || "Attachment";
  // Never surface raw file ids to end-users as the display name.
  if (typeof value === "string") return fallback || "Attachment";
  if (typeof value === "object") {
    return value.name || value.fileName || value.filename || value.originalName || fallback || "Attachment";
  }
  return fallback || "Attachment";
};

const extractFileUrl = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.includes("://")) return value;
  if (typeof value === "object") {
    return (
      value.url ||
      value.uri ||
      value.publicUrl ||
      value.fileUrl ||
      value.downloadUrl ||
      value.signedUrl ||
      value.file?.url ||
      value.file?.uri ||
      ""
    );
  }
  return "";
};

const resolveSignedUrlPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (Array.isArray(payload) && payload.length > 0) return payload[0];
  if (Array.isArray(payload?.signedUrls) && payload.signedUrls.length > 0) return payload.signedUrls[0];
  if (typeof payload?.url === "string" && payload.url.trim()) return payload.url;
  return null;
};

export default function HelperProfessionProfileDetailsPage() {
  const { professionProfileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatDateTime } = useFormattedDate();
  const { updateHelperProfileStatus } = useUserManagement();

  const seeded = location?.state?.profile && String(location.state.profile.id) === String(professionProfileId)
    ? location.state.profile
    : null;

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(seeded);
  const [error, setError] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const [signedUrlByFileId, setSignedUrlByFileId] = useState({});
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoaded, setRequirementsLoaded] = useState(false);
  const [requirementsError, setRequirementsError] = useState(null);

  const fetchSignedUrlForFileId = async (fileId) => {
    if (!fileId) return null;
    const endpoint = `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(fileId)}`;
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions] signed url request", { fileId, endpoint });
      const response = await apiClient.get(endpoint);
      // eslint-disable-next-line no-console
      console.log("[helper-professions] signed url response", { fileId, data: response?.data });
      return resolveSignedUrlPayload(response?.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[helper-professions] signed url fetch failed", {
        fileId,
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      return null;
    }
  };

  const loadProfile = useCallback(async () => {
    if (!professionProfileId) return;
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions-details] fetch profile", { professionProfileId });
      const res = await olreadiaService.professionProfiles.get(professionProfileId);
      // eslint-disable-next-line no-console
      console.log("[helper-professions-details] fetch response", res);
      setProfile(res);
      return res;
    } catch (err) {
      setError(err?.message || "Failed to load profession profile");
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [professionProfileId]);

  useEffect(() => {
    (async () => {
      const loaded = await loadProfile();
      const userId = loaded?.userId || loaded?.helperId;
    })();
  }, [loadProfile]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const sectorCode = profile?.sector?.code || profile?.sectorCode || null;
      if (!sectorCode) {
        setRequirements([]);
        setRequirementsLoaded(false);
        setRequirementsError(null);
        return;
      }
      setRequirementsLoading(true);
      setRequirementsError(null);
      try {
        const list = await olreadiaService.professionProfiles.listRequirements({
          sector: sectorCode,
        });
        const safe = Array.isArray(list) ? list : [];
        if (!cancelled) {
          setRequirements(safe);
          setRequirementsLoaded(true);
          setRequirementsError(null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[helper-professions-details] requirements fetch failed", err);
        if (!cancelled) {
          setRequirements([]);
          setRequirementsLoaded(false);
          setRequirementsError(err?.message || "Failed to load requirement rules");
        }
      } finally {
        if (!cancelled) setRequirementsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [profile?.sector?.code, profile?.sectorCode]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const docIds = [
        ...toArray(profile?.documents).map((d) => extractFileId(d)).filter(Boolean),
        ...toArray(profile?.certifications).map((c) => extractFileId(c?.fileId)).filter(Boolean),
      ]
        .filter(Boolean);

      const unique = [...new Set(docIds)];
      if (!unique.length) return;

      const entries = await Promise.all(unique.map(async (fileId) => [fileId, await fetchSignedUrlForFileId(fileId)]));
      if (cancelled) return;
      const next = {};
      entries.forEach(([fid, url]) => {
        if (url) next[fid] = url;
      });
      setSignedUrlByFileId((prev) => ({ ...prev, ...next }));
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const openPreview = (name, url) => {
    if (!url) return;
    if (!isImageUrl(url) && !isPdfUrl(url)) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    if (isPdfUrl(url)) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    setPreviewTitle(name || "Attachment");
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const requirementsStatus = useMemo(() => {
    const now = dayjs();
    const required = (Array.isArray(requirements) ? requirements : [])
      .filter((r) => r?.active !== false)
      .filter((r) => r?.required !== false);

    const requiredCount = required.length;

    const missing = required.filter((r) => {
      const type = String(r?.requirementType || "").toUpperCase();
      const code = r?.code;
      if (!type || !code) return false;
      if (type === "DOCUMENT") return !requirementSatisfiedByDocuments(profile, code);
      if (type === "CERTIFICATION") return !requirementSatisfiedByCertifications(profile, code);
      if (type === "INFORMATION") return !requirementSatisfiedByInformation(profile, code);
      return false;
    });

    const expiredCerts = (toArray(profile?.certifications) || [])
      .map((c) => ({ name: c?.name || "Certification", expiry: parseMaybeDate(c?.expiryDate) }))
      .filter((c) => c.expiry && c.expiry.isBefore(now))
      .map((c) => c.name);

    const ready = requirementsLoaded && !requirementsLoading && !requirementsError;

    return {
      missing,
      expiredCerts,
      ready,
      requiredCount,
      ok: ready && requiredCount > 0 && missing.length === 0 && expiredCerts.length === 0,
    };
  }, [profile, requirements, requirementsLoaded, requirementsLoading, requirementsError]);

  const handleStatusAction = async (nextStatus, label) => {
    const userId = profile?.userId || profile?.helperId;
    if (!userId) return;
    let notes = null;

    const needsNotes = label === "Reject" || label === "Request update" || label === "Suspend";

    await new Promise((resolve) => {
      let value = "";
      Modal.confirm({
        title: `${label} helper?`,
        icon: <ExclamationCircleOutlined />,
        content: needsNotes ? (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Add a note for the helper/admin log (optional).</Text>
            <div style={{ marginTop: 8 }}>
              <Input.TextArea rows={3} onChange={(e) => (value = e.target.value)} />
            </div>
          </div>
        ) : (
          <Text type="secondary">Are you sure you want to continue?</Text>
        ),
        okText: "Yes",
        cancelText: "No",
        okButtonProps: { danger: label === "Reject" || label === "Suspend" },
        onOk: async () => {
          notes = needsNotes ? value?.trim() || null : null;
          try {
            await updateHelperProfileStatus(userId, nextStatus, notes);
            message.success(`Helper status updated to ${nextStatus}.`);
            resolve(true);
          } catch (err) {
            message.error(err?.message || "Failed to update helper status");
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  const headerTitle = profile?.profileName || profile?.sector?.name || profile?.sector?.code || "Profession Profile";
  const sectorLabel = profile?.sector?.name || profile?.sector?.code || profile?.sectorCode || "-";
  const actionsMenu = {
    items: [
      {
        key: "approve",
        label: "Approve",
        icon: <CheckOutlined />,
        onClick: () => handleStatusAction("APPROVED", "Approve"),
      },
      {
        key: "verify",
        label: "Verify",
        icon: <CheckOutlined />,
        onClick: () => handleStatusAction("VERIFIED", "Verify"),
      },
      {
        key: "request",
        label: "Request update",
        icon: <ExclamationCircleOutlined />,
        onClick: () => handleStatusAction("PENDING", "Request update"),
      },
      {
        type: "divider",
      },
      {
        key: "suspend",
        label: "Suspend",
        danger: true,
        icon: <CloseOutlined />,
        onClick: () => handleStatusAction("SUSPENDED", "Suspend"),
      },
      {
        key: "reject",
        label: "Reject",
        danger: true,
        icon: <CloseOutlined />,
        onClick: () => handleStatusAction("TERMINATED", "Reject"),
      },
    ],
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="id-badge"
          title={headerTitle}
          subtitle={`Sector: ${sectorLabel}`}
          transform="shrink-2"
          className="flex-1"
        />
        <Space>
          {profile ? (
            <Dropdown menu={actionsMenu} trigger={["click"]}>
              <Button type="primary">
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          ) : null}
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadProfile}>
              Retry
            </Button>
          }
        />
      ) : null}

      {!profile && !loading ? <Empty description="Profession profile not found" /> : null}

      {profile ? (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Space direction="vertical" size={16} style={{ width: "100%", maxWidth: 1100 }}>
              <Card style={{ borderRadius: 12 }} loading={loading} title="Overview" styles={{ body: { padding: 12 } }}>
                <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                  <Space wrap>
                    {profile?.profileType ? <Tag>{String(profile.profileType)}</Tag> : null}
                    {sectorLabel ? <Tag>{sectorLabel}</Tag> : null}
                    {requirementsLoading ? (
                      <Tag>Checking requirements…</Tag>
                    ) : requirementsError ? (
                      <Tag color="red">Requirements unavailable</Tag>
                     ) : requirementsStatus.requiredCount === 0 ? (
                      <Tag>No requirement rules configured</Tag>
                    ) : requirementsStatus.ok ? (
                      <Tag color="green">All requirements met</Tag>
                    ) : (
                      <Tag color="orange">Missing / expired</Tag>
                    )}
                  </Space>
                  <Space wrap>
                    <Tag color={toArray(profile?.documents).length ? "blue" : "default"}>
                      Docs: {toArray(profile?.documents).length}
                    </Tag>
                    <Tag color={toArray(profile?.certifications).length ? "blue" : "default"}>
                      Certs: {toArray(profile?.certifications).length}
                    </Tag>
                  </Space>
                </Space>

                {requirementsStatus.ready && requirementsStatus.requiredCount > 0 && !requirementsStatus.ok ? (
                  <>
                    <Divider style={{ marginBlock: 12 }} />
                    <Alert
                      type="warning"
                      showIcon
                      message="Requirements not met"
                      description={
                        <Space direction="vertical" size={6} style={{ width: "100%" }}>
                          {requirementsStatus.missing.length ? (
                            <div>
                              <Text strong>Missing</Text>
                              <div style={{ marginTop: 6 }}>
                                <Space wrap>
                                  {requirementsStatus.missing.slice(0, 12).map((r) => (
                                    <Tag key={String(r?.id || r?.code)} color="orange">
                                      {r?.code}
                                    </Tag>
                                  ))}
                                </Space>
                                {requirementsStatus.missing.length > 12 ? (
                                  <div style={{ marginTop: 6 }}>
                                    <Text type="secondary">
                                      +{requirementsStatus.missing.length - 12} more
                                    </Text>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                          {requirementsStatus.expiredCerts.length ? (
                            <div>
                              <Text strong>Expired certifications</Text>
                              <div style={{ marginTop: 6 }}>
                                <Space wrap>
                                  {requirementsStatus.expiredCerts.slice(0, 8).map((name, idx) => (
                                    <Tag key={`${name}-${idx}`} color="red">
                                      {name}
                                    </Tag>
                                  ))}
                                </Space>
                              </div>
                            </div>
                          ) : null}
                          {requirementsLoading ? (
                            <Text type="secondary">Loading requirement rules…</Text>
                          ) : null}
                        </Space>
                      }
                      style={{ marginTop: 12 }}
                    />
                  </>
                ) : null}

                <Divider style={{ marginBlock: 12 }} />

                <Descriptions size="small" column={2} labelStyle={{ width: 130 }}>
                  <Descriptions.Item label="Profile ID">
                    <CopyableText value={profile?.id} showTooltip={false} stopPropagation={false} />
                  </Descriptions.Item>
                  <Descriptions.Item label="User ID">
                    <CopyableText value={profile?.userId || profile?.helperId} showTooltip={false} stopPropagation={false} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Name">{profile?.userFullName || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Email">{profile?.userEmail || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{profile?.userPhone || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Summary">{profile?.profileSummary || "-"}</Descriptions.Item>
                </Descriptions>
              </Card>

              <Card style={{ borderRadius: 12 }} title="Background" styles={{ body: { padding: 12 } }}>
                <Descriptions size="small" column={2} labelStyle={{ width: 150 }}>
                  <Descriptions.Item label="Experience">
                    {profile?.experience || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Education Level">
                    {profile?.educationLevel || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Previous Regions" span={2}>
                    {Array.isArray(profile?.previousRegionsWorkedIn) && profile.previousRegionsWorkedIn.length
                      ? profile.previousRegionsWorkedIn.join(", ")
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card style={{ borderRadius: 12 }} title="Documents" styles={{ body: { padding: 12 } }}>
                {toArray(profile?.documents).length ? (
                  <Row gutter={[12, 12]}>
                    {toArray(profile.documents)
                      .map(normalizeAttachment)
                      .filter(Boolean)
                      .map((doc, idx) => {
                        const fid = extractFileId(doc);
                        const name = doc?.caption || extractFileName(doc, `Document ${idx + 1}`);
                        const directUrl = extractFileUrl(doc);
                        const url = directUrl || (fid ? (signedUrlByFileId?.[fid] || `${STORAGE_BASE_URL}/files/${fid}`) : "");
                        const isImage = isImageUrl(url);
                        const key = fid || `${name}-${url}` || name;
                        if (!url) return null;
                        return (
                          <Col key={key} xs={12} sm={8} md={6} lg={6}>
                            <Card
                              size="small"
                              hoverable
                              style={{ borderRadius: 12 }}
                              styles={{ body: { padding: 10 } }}
                              onClick={() => openPreview(name, url)}
                            >
                              {isImage ? (
                                <Image
                                  src={url}
                                  alt={name}
                                  preview={false}
                                  style={{ width: "100%", height: 92, objectFit: "cover", borderRadius: 10 }}
                                />
                              ) : (
                                <div style={{ height: 92, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Tag>FILE</Tag>
                                </div>
                              )}
                              <div style={{ marginTop: 8 }}>
                                <Text ellipsis={{ tooltip: name }} style={{ fontSize: 12 }}>
                                  {name}
                                </Text>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                  </Row>
                ) : (
                  <Text type="secondary">No documents uploaded.</Text>
                )}
              </Card>

              <Card style={{ borderRadius: 12 }} title="Certifications" styles={{ body: { padding: 12 } }}>
                {Array.isArray(profile?.certifications) && profile.certifications.length ? (
                  <Table
                    rowKey={(r, i) => `${r?.name || "cert"}-${i}`}
                    size="small"
                    pagination={false}
                    tableLayout="auto"
                    scroll={{ x: "max-content" }}
                    columns={[
                      { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "-" },
                      { title: "Issuer", dataIndex: "issuer", key: "issuer", render: (v) => v || "-" },
                      { title: "Cert #", dataIndex: "certificationNumber", key: "certificationNumber", render: (v) => v || "-" },
                      {
                        title: "Expiry",
                        dataIndex: "expiryDate",
                        key: "expiryDate",
                        render: (v) => {
                          const d = parseMaybeDate(v);
                          if (!d) return "-";
                          const expired = d.isBefore(dayjs());
                          return <Tag color={expired ? "red" : "green"}>{d.format("MMM D, YYYY")}</Tag>;
                        },
                      },
                      {
                        title: "File",
                        key: "file",
                        render: (_, r) => {
                          const fileId = r?.fileId || null;
                          const directUrl = extractFileUrl(r);
                          const url = directUrl || (fileId ? (signedUrlByFileId?.[fileId] || `${STORAGE_BASE_URL}/files/${fileId}`) : "");
                          if (!url) return "-";
                          return (
                            <Button type="link" onClick={() => openPreview(r?.name || "Certification", url)} style={{ paddingInline: 0 }}>
                              Preview
                            </Button>
                          );
                        },
                      },
                    ]}
                    dataSource={profile.certifications}
                  />
                ) : (
                  <Text type="secondary">No certifications provided.</Text>
                )}
              </Card>

              <Card style={{ borderRadius: 12 }} title="References" styles={{ body: { padding: 12 } }}>
                {Array.isArray(profile?.references) && profile.references.length ? (
                  <Table
                    rowKey={(r, i) => `ref-${i}`}
                    size="small"
                    pagination={false}
                    tableLayout="auto"
                    scroll={{ x: "max-content" }}
                    columns={[
                      { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "-" },
                      { title: "Relationship", dataIndex: "relationship", key: "relationship", render: (v) => v || "-" },
                      { title: "Phone", dataIndex: "phone", key: "phone", render: (v) => v || "-" },
                      { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "-" },
                      { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
                    ]}
                    dataSource={profile.references}
                  />
                ) : (
                  <Text type="secondary">No references provided.</Text>
                )}
              </Card>

              <Card style={{ borderRadius: 12 }} title="Work Experience History" styles={{ body: { padding: 12 } }}>
                {Array.isArray(profile?.workExperienceHistory) && profile.workExperienceHistory.length ? (
                  <Table
                    rowKey={(r, i) => `work-${i}`}
                    size="small"
                    pagination={false}
                    tableLayout="auto"
                    scroll={{ x: "max-content" }}
                    columns={[
                      { title: "Organization", dataIndex: "organization", key: "organization", render: (v) => v || "-" },
                      { title: "Role", dataIndex: "role", key: "role", render: (v) => v || "-" },
                      { title: "Location", dataIndex: "location", key: "location", render: (v) => v || "-" },
                      { title: "Start", dataIndex: "startDate", key: "startDate", render: (v) => v || "-" },
                      { title: "End", dataIndex: "endDate", key: "endDate", render: (v) => v || "-" },
                      { title: "Current", dataIndex: "current", key: "current", align: "center", render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
                      { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
                    ]}
                    dataSource={profile.workExperienceHistory}
                  />
                ) : (
                  <Text type="secondary">No work history provided.</Text>
                )}
              </Card>

              <Card style={{ borderRadius: 12 }} title="Education" styles={{ body: { padding: 12 } }}>
                {Array.isArray(profile?.education) && profile.education.length ? (
                  <Table
                    rowKey={(r, i) => `edu-${i}`}
                    size="small"
                    pagination={false}
                    tableLayout="auto"
                    scroll={{ x: "max-content" }}
                    columns={[
                      { title: "Institution", dataIndex: "institution", key: "institution", render: (v) => v || "-" },
                      { title: "Program", dataIndex: "program", key: "program", render: (v) => v || "-" },
                      { title: "Level", dataIndex: "educationLevel", key: "educationLevel", render: (v) => v || "-" },
                      { title: "Year", dataIndex: "graduationYear", key: "graduationYear", align: "right", render: (v) => v || "-" },
                      { title: "Notes", dataIndex: "notes", key: "notes", ellipsis: true, render: (v) => (v ? <Text ellipsis={{ tooltip: v }}>{v}</Text> : "-") },
                    ]}
                    dataSource={profile.education}
                  />
                ) : (
                  <Text type="secondary">No education history provided.</Text>
                )}
              </Card>

              <Card style={{ borderRadius: 12 }} title="Interests" styles={{ body: { padding: 12 } }}>
                <Descriptions size="small" column={1} labelStyle={{ width: 160 }}>
                  <Descriptions.Item label="Services">
                    {Array.isArray(profile?.interestedServices) && profile.interestedServices.length
                      ? profile.interestedServices
                          .map((s) =>
                            typeof s === "string"
                              ? s
                              : s?.briefName || s?.name || s?.code || s?.id,
                          )
                          .filter(Boolean)
                          .join(", ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Locations">
                    {Array.isArray(profile?.interestedLocations) && profile.interestedLocations.length
                      ? profile.interestedLocations.map(formatLocationInterest).filter(Boolean).join(" • ")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Shift Availability">
                    {Array.isArray(profile?.interestedShifts) && profile.interestedShifts.length
                      ? profile.interestedShifts.map((s) => String(s)).join(", ")
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Space>
          </Col>
        </Row>
      ) : null}

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={900}
        zIndex={100500}
        destroyOnClose
        centered
      >
        {previewUrl ? (
          <div style={{ textAlign: "center" }}>
            <Image
              src={previewUrl}
              alt={previewTitle}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
              preview={false}
            />
          </div>
        ) : (
          <Empty description="No preview available" />
        )}
      </Modal>
    </div>
  );
}
